<template>
  <div class="cat-formatted-input">
    <cat-label
      v-if="!labelHandled"
      :labelId="id + '-input-lbl'"
      :label="label"
      :forId="id"
      :optional="!mandatory"
      :display-optional="showOptionals"
      class="placeholder"
    ></cat-label>
    <input
      v-if="inputType !== 'textarea'"
      :id="id"
      v-model="displayValue"
      v-facade="inputMask"
      :placeholder="placeholder"
      :data-label-type="placeholder ? 'placeholder' : null"
      :data-label="placeholder"
      :type="inputType"
      :aria-invalid="errors"
      :aria-required="mandatory"
      :aria-label="offscreenLabel"
      :readonly="readonly"
      :required="errors"
      :maxlength="maxLength"
      :class="errors ? 'invalidField' : ''"
      :data-masked="inputMask ? 'true' : null"
      @change="_onChange"
      @input="_onInput"
      @blur="_onBlur"
      @keydown="_keydown"
      @paste="_onPaste"
      @focus="_onFocus"
      @keyup="_keyup"
      @keypress="_keypress"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
// Imported globally
// import { facade } from "vue-input-facade";
import CatLabel from "@/components/utilities/cat-label";

export default defineComponent({
  setup() {
    // Inject the global value
    const showOptionals = inject("showOptionals", true);

    return {
      showOptionals
    };
  },
  name: "CatFormattedInput",
  // directives: { facade },
  components: { CatLabel },
  props: {
    placeholder: {
      type: String,
      description: "Sets the placeholder text for a field."
    },
    mandatory: {
      type: Boolean,
      description: "Sets aria-required attributes for input fields and label."
    },
    id: {
      type: [String, Number],
      description: "Sets the ID attribute."
    },
    label: {
      type: String,
      description: "Sets the label to display above input fields."
    },
    offscreenLabel: {
      type: String,
      default: null,
      description: "Label to be used by accessibility."
    },
    value: {
      type: [String, Number],
      description: "Defines the initial default value for the associated field."
    },
    rawValue: [String, Number],
    errors: {
      type: Boolean,
      default: false
    },
    labelHandled: {
      type: Boolean,
      default: false,
      description:
        "Flag to indicate if requiring an input field label. True means the input label is already defined somewhere else. False means a field label will rendered for this input."
    },
    readonly: {
      type: [Boolean],
      description:
        "Defines whether a user can input data into the associated field."
    },
    inputType: {
      type: String,
      default: "text", // The usual input types and also textarea
      description:
        "Defines the type of data the field will accept from the standard HTML options for input. For example: text, number, password. For auto-format numbers, please see cat-attribute-number or cat-attribute-decimal."
    },
    mask: {
      type: String,
      description:
        "Defines characters which will be accepted by the input element."
    },
    inputMask: {
      type: [String, Array],
      default: ""
    },
    regex: {
      type: String
    },
    maxLength: {
      type: Number,
      description: "Sets the maximum number of characters that can be entered."
    },
    title: {
      type: String
    }
  },
  computed: {
    displayValue: {
      get: function () {
        if (
          !this.hasFocus &&
          this.rawValue !== this.model &&
          this.model === this.working
        ) {
          // User has made a change to existing value but it has not been formatted yet.
          return this.working;
        }
        return this.value;
      },
      set: function (newvalue) {
        var val = this.cleanseInput(newvalue);
        // Set values only if allowed
        if (this.maskMatch(val) && this.regexMatch(val)) {
          this.model = newvalue;
          this.working = val;
        }
      }
    }
  },
  data: function () {
    return {
      model: this.rawValue,
      working: this.rawValue,
      hasFocus: false,
      keyWhitelist: [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "Tab",
        "Enter"
      ]
    };
  },
  methods: {
    _keydown: function (e) {
      let prevented = false;
      if (
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        this.keyWhitelist.indexOf(e.key) === -1
      ) {
        if (
          e.target.selectionStart == e.target.selectionEnd &&
          this.model &&
          this.maxLength &&
          this.model.length >= this.maxLength
        ) {
          e.preventDefault();
          prevented = true;
        } else if (this.mask && e.key.length == 1) {
          if (this.mask && this.mask.indexOf(e.key) == -1) {
            e.preventDefault();
            prevented = true;
          }
        }
        if (!prevented && !this.regexMatch(this.model)) {
          e.preventDefault();
          prevented = true;
        }
      }
      if (!prevented) {
        this.$emit("ui-key-down", {
          event: e,
          value: this.model,
          id: this.id ? this.id : null
        });
      }
    },
    _keyup: function (event) {
      this.$emit("ui-key-up", {
        event: event,
        value: this.model,
        id: this.id ? this.id : null
      });
    },
    _keypress: function (event) {
      this.$emit("ui-key-press", {
        event: event,
        value: this.model,
        id: this.id ? this.id : null
      });
    },
    _onChange: function (event) {
      if (!this.maskMatch(this.model)) {
        this.model = this.working;
        return false;
      }
      if (!this.regexMatch(this.model)) {
        this.model = this.working;
        return false;
      }
      this.$emit("ui-change", {
        event: event,
        value: this.model,
        id: this.id ? this.id : null
      });
    },
    _onFocus: function (event) {
      this.hasFocus = true;
      this.$emit("ui-focus", {
        event: event,
        value: this.model,
        id: this.id ? this.id : null
      });
    },
    _onBlur: function (event) {
      this.hasFocus = false;
      this.$emit("ui-blur", {
        event: event,
        value: this.model,
        id: this.id ? this.id : null
      });
    },
    _onPaste: function (e) {
      if (this.mask && e.clipboardData) {
        if (e.clipboardData && e.clipboardData.getData) {
          var pastedText = "";
          if (window.clipboardData && window.clipboardData.getData) {
            // IE
            pastedText = window.clipboardData.getData("Text");
          } else if (e.clipboardData && e.clipboardData.getData) {
            pastedText = e.clipboardData.getData("text/plain");
          }
        }
        // Check if its  valid input
        if (!this.maskMatch(pastedText) || !this.regexMatch(pastedText)) {
          // cleanse
          pastedText = this.cleanseInput(pastedText);
          // Check again for validity
          if (!this.maskMatch(pastedText) || !this.regexMatch(pastedText)) {
            e.preventDefault();
            e.stopPropagation();
            this.working = "";
            this.model = this.working;
            return false;
          }
        }
      }
      return true;
    },
    _onInput: function (event) {
      var val = this.cleanseInput(event.target.value);
      if (!this.maskMatch(val)) {
        this.model = this.working;
        this.$forceUpdate();
        return;
      }
      if (!this.regexMatch(val)) {
        this.model = this.working;
        this.$forceUpdate();
        return;
      }
      this.model = val; //two way binding
      this.working = val;
      this.$emit("ui-input", {
        event: event,
        value: this.model,
        id: this.id ? this.id : null
      });
    },
    cleanseInput: function (inputText) {
      var cleanText = "".split("");
      if (this.mask && inputText) {
        for (let i = 0; i < inputText.length; i++) {
          if (this.mask.indexOf(inputText.charAt(i)) >= 0) {
            cleanText.push(inputText.charAt(i));
          }
        }
      }
      return cleanText.join("");
    },
    maskMatch: function (val) {
      if (this.mask && val) {
        for (let i = 0; i < val.length; i++) {
          if (this.mask.indexOf(val.charAt(i)) == -1) {
            return false;
          }
        }
      }
      return true;
    },
    regexMatch: function (val) {
      if (this.$props.regex) {
        let re = RegExp(this.$props.regex);
        return re.test(val);
      }
      return true;
    }
  },
  mounted: function () {
    const input = this.$el.querySelector("input, textarea");
    if (!this.value) {
      // need to check that checkAndTriggerAutoFillEvent is available before calling.
      // currently it isn't available to unit tests
      if (input && input.checkAndTriggerAutoFillEvent) {
        input.checkAndTriggerAutoFillEvent();
        input.setAttribute("value", "");
      }
    } else {
      // set value on attribute so autofill event doesn't fire mistakenly.
      input.setAttribute("value", this.model);
    }
  }
});
</script>
